import type { FetchArgs } from '@reduxjs/toolkit/dist/query/fetchBaseQuery';
import type { BaseQueryApi, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Mutex } from 'async-mutex';

import type { SessionData } from '../types/session';

import { baseQuery } from './base-query';
import { ENDPOINTS } from './endpoints';
import { invalidateSession } from './invalidate-session';
import { resetSession } from './reset-session';

const USER_NOT_FOUND = 'user_not_found';

function isPlanMigrationRequired(
    error: FetchBaseQueryError | undefined,
): boolean {
    return (
        typeof error?.status === 'number' &&
        error.status === 403 &&
        !!error.data &&
        typeof error.data === 'object' &&
        'error' in error.data &&
        (error.data as Record<string, unknown>)['error'] ===
            'plan_migration_required'
    );
}

/**
 * Checks if the error is an unauthorized status that requires token refresh
 * - 401: Standard unauthorized response from the API
 * - 403: Forbidden response from CMS when token validation fails
 */
function isUnauthorizedStatus(error: FetchBaseQueryError | undefined): boolean {
    return (
        typeof error?.status === 'number' &&
        (error.status === 401 || error.status === 403)
    );
}

/**
 * Check if the endpoint is an authentication endpoint that should not trigger session invalidation popup
 */
function isAuthEndpoint(args: string | FetchArgs): boolean {
    const url = typeof args === 'string' ? args : args.url;

    const authEndpoints = [
        ENDPOINTS.auth.signIn,
        ENDPOINTS.auth.signUp,
        ENDPOINTS.auth.google,
        ENDPOINTS.auth.confirmEmail,
        ENDPOINTS.auth.initiatePasswordRecovery,
        ENDPOINTS.auth.resetPassword,
        ENDPOINTS.auth.checkResetPasswordParams,
    ];

    return authEndpoints.some((endpoint) => url.includes(endpoint));
}

function isRefreshTokensResult(value: unknown): value is SessionData {
    return (
        typeof value === 'object' &&
        value !== null &&
        'access' in value &&
        'refresh' in value
    );
}

export function isUserNotFound(
    error: FetchBaseQueryError | undefined,
): boolean {
    const code =
        !!error?.data &&
        typeof error.data === 'object' &&
        'code' in error.data &&
        error.data.code;

    return (
        typeof error?.status === 'number' &&
        (error.status === 401 || error.status === 403) &&
        code === USER_NOT_FOUND
    );
}

const mutex = new Mutex();

export async function baseQueryWithReauth(
    args: string | FetchArgs,
    api: BaseQueryApi,
    extraOptions: {},
    queryFn = baseQuery,
): Promise<Awaited<ReturnType<typeof baseQuery>>> {
    await mutex.waitForUnlock();

    let result = await queryFn(args, api, extraOptions);

    if (isPlanMigrationRequired(result.error)) {
        return result;
    }

    if (isUnauthorizedStatus(result.error)) {
        const isAuthEndpointRequest = isAuthEndpoint(args);

        if (isUserNotFound(result.error)) {
            api.dispatch(
                invalidateSession({
                    isAuthEndpointFailure: isAuthEndpointRequest,
                }),
            );
        } else if (!mutex.isLocked()) {
            const release = await mutex.acquire();

            try {
                const { refreshToken } = (api.getState() as AppStateSchema)
                    .session;

                // Always use baseQuery for token refresh regardless of the queryFn
                // This ensures consistent token refresh behavior
                const refreshTokensResult = await baseQuery(
                    {
                        url: ENDPOINTS.auth.refresh,
                        body: { refresh: refreshToken },
                        method: 'POST',
                    },
                    { ...api, endpoint: ENDPOINTS.auth.refresh },
                    extraOptions,
                );

                if (isRefreshTokensResult(refreshTokensResult.data)) {
                    api.dispatch(resetSession(refreshTokensResult.data));

                    result = await queryFn(args, api, extraOptions);
                } else {
                    api.dispatch(
                        invalidateSession({
                            isAuthEndpointFailure: isAuthEndpointRequest,
                        }),
                    );
                }
            } finally {
                release();
            }
        } else {
            await mutex.waitForUnlock();

            result = await queryFn(args, api, extraOptions);
        }
    }

    return result;
}
